import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EmployeeActions } from './employee.actions';
import { catchError, map, mergeMap, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '@core/services/employee.service';
import { AuthenticationService } from '@core/services/auth.service';
import { ROLES_NAME } from '@core/interface/employee';
import { logout } from '../authentication/authentication.actions';

@Injectable()
export class EmployeeEffects {
  private actions$ = inject(Actions);
  private employeeService = inject(EmployeeService);
  private authService = inject(AuthenticationService);
  private translateService = inject(TranslateService);
  getEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.loadEmployees),
      mergeMap((data) =>
        this.employeeService.getEmployees(data).pipe(
          map((employees) =>
            employees.success
              ? EmployeeActions.loadEmployeesSuccess(employees)
              : EmployeeActions.loadEmployeesFailure(employees)
          ),
          catchError((error) =>
            of(
              EmployeeActions.loadEmployeesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.addEditEmployee),
      mergeMap((data) =>
        this.employeeService.addEmployee(data.employee).pipe(
          map((employee) => {
            if (employee.success) {
              return data.employee.id
                ? EmployeeActions.updateEmployeeSuccess(employee)
                : EmployeeActions.addEmployeeSuccess(employee);
            } else return EmployeeActions.addEmployeeFailure(employee);
          }),
          catchError((error) =>
            of(
              EmployeeActions.addEmployeeFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  deleteEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.deleteEmployee),
      mergeMap((data) =>
        this.employeeService.deleteEmployee(data.id).pipe(
          map((response) =>
            response.success
              ? EmployeeActions.deleteEmployeeSuccess({
                  ...response,
                  id: data.id,
                })
              : EmployeeActions.deleteEmployeeFailure(response)
          ),
          catchError((error) =>
            of(
              EmployeeActions.deleteEmployeeFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  sendInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.sendInvite),
      mergeMap((data) =>
        this.authService.sendInvite(data.user_id).pipe(
          map((employees) =>
            employees.success
              ? EmployeeActions.sendInviteSuccess(employees)
              : EmployeeActions.sendInviteFailure(employees)
          ),
          catchError((error) =>
            of(
              EmployeeActions.sendInviteFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getEmployeesByRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.loadEmployeesByRole),
      mergeMap((data) =>
        this.employeeService
          .getEmployeeByRole(data.role_id, data.master_company_id)
          .pipe(
          map((employees) =>
            employees.success
              ? EmployeeActions.loadEmployeesByRoleSuccess({
                  employees,
                  roleName: data.roleName as ROLES_NAME,
                })
              : EmployeeActions.loadEmployeesByRoleFailure({
                  error: employees,
                  roleName: data.roleName as ROLES_NAME,
                })
          ),
          catchError((error) =>
            of(
              EmployeeActions.loadEmployeesByRoleFailure({
                error: {
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                },
                roleName: data.roleName as ROLES_NAME,
              })
            )
          )
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.employeesLogout),
      mergeMap(() =>
        this.employeeService.logout().pipe(
          map((employees) => {
            logout();
            return employees.success
              ? EmployeeActions.employeesLogoutSuccess(employees)
              : EmployeeActions.employeesLogoutFailure(employees);
          }),
          catchError((error) =>
            of(
              EmployeeActions.employeesLogoutFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  profile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.employeeProfile),
      mergeMap(() =>
        this.employeeService.profile().pipe(
          map((employees) =>
            employees.success
              ? EmployeeActions.employeeProfileSuccess(employees)
              : EmployeeActions.employeeProfileFailure(employees)
          ),
          catchError((error) =>
            of(
              EmployeeActions.employeeProfileFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.changePassword),
      mergeMap((data) =>
        this.employeeService.chnagePassword(data).pipe(
          map((password) =>
            password.success
              ? EmployeeActions.changePasswordSuccess(password)
              : EmployeeActions.changePasswordFailure(password)
          ),
          catchError((error) =>
            of(
              EmployeeActions.changePasswordFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getEmploymentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.loadEmploymentType),
      mergeMap(() =>
        this.employeeService.getEmploymentTypes().pipe(
          map((response) =>
            response.success
              ? EmployeeActions.loadEmploymentTypeSuccess(response)
              : EmployeeActions.loadEmploymentTypeFailure(response)
          ),
          catchError((error) =>
            of(
              EmployeeActions.loadEmploymentTypeFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addEmploymentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.addEditEmploymentType),
      mergeMap((data) =>
        this.employeeService.addEmploymentType(data).pipe(
          map((employee) => {
            if (employee.success) {
              return data.id
                ? EmployeeActions.updateEmploymentTypeSuccess(employee)
                : EmployeeActions.addEmploymentTypeSuccess(employee);
            } else return EmployeeActions.addEmploymentTypeFailure(employee);
          }),
          catchError((error) =>
            of(
              EmployeeActions.addEmploymentTypeFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getWorkSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.loadWorkSchedule),
      mergeMap(() =>
        this.employeeService.getWorkSchedules().pipe(
          map((response) =>
            response.success
              ? EmployeeActions.loadWorkScheduleSuccess(response)
              : EmployeeActions.loadWorkLocationFailure(response)
          ),
          catchError((error) =>
            of(
              EmployeeActions.loadWorkLocationFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addWorkSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.addEditWorkSchedule),
      mergeMap((data) =>
        this.employeeService.addWorkSchedule(data).pipe(
          map((employee) => {
            if (employee.success) {
              return data.id
                ? EmployeeActions.updateWorkScheduleSuccess(employee)
                : EmployeeActions.addWorkScheduleSuccess(employee);
            } else return EmployeeActions.addWorkScheduleFailure(employee);
          }),
          catchError((error) =>
            of(
              EmployeeActions.addWorkScheduleFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getWorkLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.loadWorkLocation),
      mergeMap(() =>
        this.employeeService.getWorkLocations().pipe(
          map((response) =>
            response.success
              ? EmployeeActions.loadWorkLocationSuccess(response)
              : EmployeeActions.loadWorkLocationFailure(response)
          ),
          catchError((error) =>
            of(
              EmployeeActions.loadWorkLocationFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addWorkLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.addEditWorkLocation),
      mergeMap((data) =>
        this.employeeService.addWorkLocation(data).pipe(
          map((employee) => {
            if (employee.success) {
              return data.id
                ? EmployeeActions.updateWorkLocationSuccess(employee)
                : EmployeeActions.addWorkLocationSuccess(employee);
            } else return EmployeeActions.addWorkLocationFailure(employee);
          }),
          catchError((error) =>
            of(
              EmployeeActions.addWorkLocationFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  changeCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmployeeActions.changeCompany),
      mergeMap((data) =>
        this.employeeService.changeCompany(data).pipe(
          map((password) =>
            password.success
              ? EmployeeActions.changeCompanySuccess(password)
              : EmployeeActions.changeCompanyFailure(password)
          ),
          catchError((error) =>
            of(
              EmployeeActions.changeCompanyFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );
}
